import React, { Suspense, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import { useGLTF } from '@react-three/drei';
import { Vector3 } from 'three';
import AnimatedResident from './AnimatedResident';
import SafeMount from './SafeMount';
import { ROOMS } from '../rooms';
import { useWorldStore, ZOOM } from '../state/worldStore';
import { useCityStore } from '../city/cityStore';
import { stateAt } from '../city/cityMotion';
import { micWords } from '../city/stageBridge';
import { setLevel } from '../city/hearing';
import { useVenueMingle, venueHello } from '../city/venueMingle';
import { playerPos } from '../game/player';
import { serverNow } from '../services/worldSocket';
import { audioEngine } from '../services/audioEngine';
import { wearingHeadphones } from '../state/headphones';
import { takeAudio, releaseAudio, onAudio } from '../services/audioPool';
import { venueAgent } from '../city/venueAgent';
import VenueAgent from './VenueAgent';
import { overKeys } from './hudStack';

// VenueStage — a SET INSIDE A VENUE (Phase 2 of API docs/VENUE_PIPELINE_PLAN.md;
// Khari, 2026-10-30: *"Venue sets: 20 minutes, back to back"*, the plaza's
// machinery made per place: *"for these performances, we should have the
// artists give intros and optional outros"*).
//
// The server books the set (API CityLifeService.ensureVenueSets: the founder
// first, then the others, each their own yes or no) and sends it in `shows` like
// a plaza set, with `inside: true` and `place` = this room's id. The street does
// not play it (CityStage skips inside shows); this room does, the plaza's way:
//   · THE MUSIC AND THE WORDS on the server clock (a late arrival hears the
//     right bar), from pooled players (services/audioPool: on iOS only a
//     tap-blessed element may start), a refused start tried again every 2 s;
//     the Blackroom's intro before each song, the Jahva House's introduction,
//     an outro only if they chose to say one. Room level, no falloff: you are in
//     the room. The player's ⏯ silences it (cityMusicOff), as at the plaza.
//   · THE STATION STEPS ASIDE for the whole set (the room hold, 'venue-set'),
//     back when it is over or when you leave.
//   · THE PERFORMER at the booth (the club) or the spoken-word corner (the
//     café): their timeline puts them in this room on spot `booth` (API
//     assignSpots, `r` = this room), the street hides them at the door
//     (CityResident), and they stand here in their own body (RoomStory's way),
//     the face moving while they sing or speak.
//   · WHAT IS ON, for the rest of the room: the wall screens (VenueScreens read
//     `venueNow`), the player bar (`stageNow`, as the plaza's) and the caption
//     of their words (`venueCaption`, VenueStageCaption below, in the DOM).
//   · PERFORM OR MINGLE (2026-10-30, API sockets/venueMingle). Khari: *"the
//     'perform or mingle' choice — mingle when the user is the only user. This
//     can mimic the radio DJ logic."* The SERVER decides from who is in the
//     room and says so (`venue_mode`, city/venueMingle). Mingling, the show
//     still runs as written (their words into the mic, from the booth) and the
//     host VISITS: they come over when they have something to say to you or
//     you tap them, stand with you a while, then go back (2026-10-31: no
//     following, see LINGER_MS); while they stand with you the set plays on
//     UNDER them (MINGLE_LEVEL); their lines arrive as
//     `venue_dj` (the radio DJ's solo brain) and play in their voice, captioned,
//     the set ducked lower again while they speak, and each goes into the chat
//     log above the player bar, where you type back (`say`; tapping them opens
//     it). When the room changes (someone comes in, you leave, the set ends) a
//     line still playing is cut at once and they walk back to the booth: one
//     voice at a time, never a body left standing where the mode no longer is.
//     ⚠ iOS ignores `volume` on media elements (city/hearing setLevel), so there
//     the set cannot play lower: it stays at room level under their lines.
//   · AGENT MODE (2026-10-31, VenueAgent): signed in, the host talks through
//     their own voice agent, one-way. Its lines arrive as `agent` lines with
//     no file (the call streams the voice): never held behind the mic (they
//     are already playing), shown at once, and "speaking" is the call's own.
//
// Mounted by MeshInterior for a room with a `booth` or `stage.spoken` (registry
// instances), inside the room group and outside its scale group, like RoomStory:
// a person is sized against the world. `k` carries the room's fit and placement
// scale to the mark, so they stand behind the decks however the room is fitted.

const HUSH_MS = 15000;       // the station goes quiet this long before the first note (the act walking on)
const SOON_MS = 5 * 60000;   // the screens say "up next" this long before
const EDGE_MS = 2500;        // fade in and out at the ends of the set
const SONG_LEVEL = 0.9;
const WORDS_LEVEL = 1;
const HOLDER = 'venue-set';
// Mingling (see the header).
const MINGLE_LEVEL = 0.4;    // the set, under a conversation
const DUCK = 0.45;           // …and lower again while the host is saying something
const MIX_EASE = 0.35;       // per 0.2 s step: the level glides, never jumps
const LINE_LEVEL = 1;
const TEXT_HOLD_MS = 2500;   // a caption-only line stays up its reading time plus this
const LINE_FRESH_MS = 15000; // a line older than this when it arrives is not played (a tab woken late)
const NEAR = 0.6;            // how far from you they stand (world units: a person is ~0.75 tall)
/* ⚠ THEY VISIT, THEY DO NOT FOLLOW (2026-10-31). Khari: *"when the user is
   alone, the artist follows them around at all times. The artist should act
   naturally. Perhaps they walk to the DJ set, they perform, etc. They are still
   approachable, and may still approach the user at times, but they should not
   steadily follow."* Mingling used to re-aim at you every time you moved 0.9
   units. Now the host stays at the booth, performing, and comes over when they
   have something to say to you (a line from the server) or when you tap them.
   They stand with you while they talk and LINGER_MS after, then go back. Walk
   off LET_GO from them and they let you go, never chasing. */
const LINGER_MS = 20000;     // after their last line, they stay with you this long
const LET_GO = 2.6;          // you walked this far from where they stand: they head back to the booth
const MIC_LEAD_MS = 12000;   // back at the booth this long before their words into the mic (the walk back)
const LINE_WAIT_MS = 45000;  // a line to you held behind the mic this long at most, then let go

const cleanText = (s) => String(s || '').replace(/\[[^\]]{1,24}\]\s*/g, '').trim();
const firstName = (n) => String(n || '').split(' ')[0] || 'them';
// How long a line takes to say (~165 wpm), for a caption-only line and until a voiced one's length is known.
const speechMs = (text) => {
  const w = String(text || '').trim().split(/\s+/).filter(Boolean).length;
  return w ? Math.min(20000, Math.max(1500, Math.round((w / 165) * 60000) + 600)) : 0;
};

// The performer: at the mark, or walking to wherever `goal` says (mingling).
// The group moves itself every frame; React only hears about walking ↔ idle.
// Exported for RoomHost (an owner in at their own room stands at the booth the same way).
export function Performer({ a, name, mark, k, light, talking, goal, onTap, onReady, groupRef = null }) {
  const npcList = useWorldStore((s) => s.npcList);
  const body = useMemo(() => (npcList || []).find((n) => Number(n?.avatarId) === Number(a) && n.rigged && !n.test) || null, [npcList, a]);
  const height = body?.height || 0.75;
  const home = useMemo(() => [mark.pos[0] * k, (mark.pos[1] || 0) * k, mark.pos[2] * k], [mark, k]);
  const g = useRef(null);
  const walkingRef = useRef(false);
  const [walking, setWalking] = useState(false);

  // Placed at the mark once; from then on they walk (a refit glides them to the new mark).
  useLayoutEffect(() => {
    const grp = g.current;
    if (!grp) return;
    grp.position.set(home[0], home[1], home[2]);
    grp.rotation.set(0, mark.rotY || 0, 0);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useFrame((_, dt) => {
    const grp = g.current;
    if (!grp) return;
    const d0 = Math.min(dt, 0.1);
    const to = goal.current;   // { x, y, z, face: { x, z } } mingling, else null = the mark
    const tx = to ? to.x : home[0]; const ty = to ? to.y : home[1]; const tz = to ? to.z : home[2];
    const dx = tx - grp.position.x; const dz = tz - grp.position.z;
    const dist = Math.hypot(dx, dz);
    const moving = dist > 0.03;
    let face;
    if (moving) {
      const step = Math.min(dist, height * 0.9 * d0);   // an unhurried walk
      grp.position.x += (dx / dist) * step;
      grp.position.z += (dz / dist) * step;
      face = Math.atan2(dx, dz);
    } else if (to?.face) {
      face = Math.atan2(to.face.x - grp.position.x, to.face.z - grp.position.z);
    } else {
      face = mark.rotY || 0;
    }
    grp.position.y += (ty - grp.position.y) * Math.min(1, d0 * 5);
    let dr = face - grp.rotation.y;
    dr = Math.atan2(Math.sin(dr), Math.cos(dr));
    grp.rotation.y += dr * Math.min(1, d0 * 6);
    if (moving !== walkingRef.current) { walkingRef.current = moving; setWalking(moving); }
  });

  return (
    <group ref={(el) => { g.current = el; if (groupRef) groupRef.current = el; }}>
      {body ? (
        // KEYED BY WHO: a different artist on the same mark is a NEW body, never
        // the old one handed another model. Kept mounted, the old body's
        // animation state (its actions, its bones' rest) carried over onto the
        // new rig and bent it (2026-11-01, Khari: "leaving one class and entering
        // another often leaves the tutor's body mangled" — every Hype-U
        // classroom is one room model, so the room stays up and only the host
        // changes).
        <SafeMount key={`${a}:${body.rigged}`} label={`venue set ${name || a}`} retryMs={30000} onRetry={() => useGLTF.clear([body.rigged, body.walk, body.idle].filter(Boolean))}>
          <Suspense fallback={null}>
            <AnimatedResident
              meshUrl={body.rigged}
              rigPose={body.pose || null}
              clipMap={body.clips || null}
              clipUrls={[body.walk, body.idle].filter(Boolean)}
              play={walking ? 'walk' : 'idle'}
              avatarId={a}
              position={[0, 0, 0]}
              rotationY={0}
              height={height}
              speaking={talking && !walking}
              indoor
              lightScale={light}
              onSelect={onTap}
              onReady={onReady}
            />
          </Suspense>
        </SafeMount>
      ) : (
        // A stand-in at their height until the body arrives.
        <mesh position={[0, height * 0.5, 0]} onClick={onTap ? (e) => { e.stopPropagation(); onTap(); } : undefined}>
          <capsuleGeometry args={[height * 0.13, height * 0.62, 4, 8]} />
          <meshStandardMaterial color="#2e2a3a" roughness={0.8} metalness={0} />
        </mesh>
      )}
    </group>
  );
}

export default function VenueStage({ roomId, k = 1 }) {
  const room = ROOMS[roomId];
  const mark = room?.booth || room?.stage?.spoken || null;
  const els = useRef(new Map());      // `${a}:${t0}:${i|which}` → { el, started, tried, playErr }
  const acc = useRef(0);
  const held = useRef(false);
  const mine = useRef({ stage: false });   // whether the player bar's "what is on" is ours to clear
  const root = useRef(null);               // this component's group: the room's own space, for "where are you"
  const goal = useRef(null);               // where a mingling host is headed (Performer reads it)
  const visit = useRef(null);              // { until } while the host has come over to you; null = at the booth
  const pending = useRef(null);            // { ln, since }: a line to you, held while the mic is theirs
  const micSoonRef = useRef(false);        // due at the mic soon (a tap then waits for the booth)
  const line = useRef(null);               // the host's line playing now { id, a, mode, name, text, el, tried, until }
  const seenLine = useRef(0);
  const mix = useRef(1);                   // the set's level multiplier, eased
  const mingRef = useRef(false);
  const tmp = useMemo(() => new Vector3(), []);
  const [perf, setPerf] = useState(null);  // { a, name } at the mark, or null
  const [talking, setTalking] = useState(false);
  const [mingling, setMingling] = useState(false);
  const [host, setHost] = useState(null);   // { a, name } of the set's host while mingling (VenueAgent)
  const lastLook = useRef(null);           // where you were and which way you faced, for venueAgent.activeAt

  // Where the set in here stands, asked as the room opens (the server also says
  // so on `enter`; this covers a room drawn after a reconnect).
  useEffect(() => { venueHello(roomId); }, [roomId]);

  const stopLine = () => {
    const L = line.current;
    if (L?.el) releaseAudio(L.el);
    line.current = null;
  };

  // Leaving the room (or the set ending): every player back to the pool, the
  // station back, and nothing left saying a set is on.
  useEffect(() => () => {
    for (const e of els.current.values()) releaseAudio(e.el);
    els.current.clear();
    stopLine();
    if (held.current) { held.current = false; audioEngine.roomQuiet(false, 900, HOLDER).catch?.(() => {}); }
    const cs = useCityStore.getState();
    const patch = { venueNow: null, venueCaption: null };
    if (mine.current.stage) patch.stageNow = null;
    mine.current.stage = false;
    if (cs.venueNow || cs.venueCaption || patch.stageNow === null) useCityStore.setState(patch);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useFrame((state, dt) => {
    acc.current += dt;
    if (acc.current < 0.2) return;
    acc.current = 0;
    const cs = useCityStore.getState();
    const t = serverNow();
    // Paused from the player bar = silent; headphones on at a table = under them (HeadphonesDock).
    const heard = (useWorldStore.getState().cityMusicOff ? 0 : 1) * (wearingHeadphones() ? 0.12 : 1);
    const shows = cs.live ? (cs.shows || []).filter((x) => x.inside && x.place === roomId) : [];
    const sh = shows.find((x) => t >= x.t0 - HUSH_MS && t < x.t1) || null;
    const next = sh ? null : shows.filter((x) => t < x.t0 && t >= x.t0 - SOON_MS).sort((p, q) => p.t0 - q.t0)[0] || null;
    const keep = new Set();
    let playing = null;   // { title, art, words }
    let caption = null;

    // ── Perform or mingle: the server's word for this set ──────────────────
    const vm = useVenueMingle.getState();
    const md = vm.modes[roomId] || null;
    const modeNow = sh && md && Number(md.a) === Number(sh.a) ? md.mode : 'perform';
    const ming = !!(sh && modeNow === 'mingle' && t >= sh.t0);
    if (ming !== mingRef.current) { mingRef.current = ming; setMingling(ming); setHost(ming ? { a: sh.a, name: sh.name } : null); }
    // Moving or looking around counts as being here (VenueAgent closes the call on a still player).
    {
      const pp = playerPos(state.camera);
      const yaw = state.camera.rotation.y;
      const lk = lastLook.current;
      if (!lk || Math.hypot(pp.x - lk.x, pp.z - lk.z) > 0.15 || Math.abs(yaw - lk.yaw) > 0.2) {
        lastLook.current = { x: pp.x, z: pp.z, yaw };
        if (lk) venueAgent.activeAt = Date.now();
      }
    }

    /* ── THE SHOW RUNS AS WRITTEN, ALONE OR NOT (2026-10-31) ─────────────────
       Khari: *"the artist abandons their intro and narratives when a single user
       is there. They should still perform those. The main difference between
       single and multi-user is the ability to interact with the artist. The
       performance of the venue/room should still remain intact, even if
       personalized."* So their words into the mic play in both modes, from the
       booth: a visit ends MIC_LEAD_MS before one (they walk back for it), no
       visit starts inside that window, and a line to you never talks over one —
       it waits until the words are done (a line already playing is cut; its text
       is in the chat). */
    const mics = sh ? micWords(sh).map(([, w]) => ({ start: sh.t0 + (w.at || 0), end: sh.t0 + (w.at || 0) + (Number(w.dur) || 3) * 1000 })) : [];
    const micNow = mics.some((m) => t >= m.start && t < m.end);
    const micSoon = mics.some((m) => t >= m.start - MIC_LEAD_MS && t < m.end);
    micSoonRef.current = micSoon;
    venueAgent.micNow = micNow;   // the agent's cues wait for their words into the mic (VenueAgent)

    // ── Their lines (API venueMingle → the radio DJ's brain) ───────────────
    const fresh = vm.line && vm.line.id !== seenLine.current ? vm.line : null;
    if (fresh) seenLine.current = fresh.id;
    if (fresh && sh && fresh.place === roomId && Number(fresh.a) === Number(sh.a) && (fresh.mode || 'perform') === modeNow && Date.now() - fresh.at < LINE_FRESH_MS) {
      pending.current = { ln: fresh, since: Date.now() };
    }
    // Held while the mic is theirs; dropped if it waited too long to still make sense.
    // An agent line is never held: its voice is already streaming from the call.
    if (pending.current && Date.now() - pending.current.since > LINE_WAIT_MS) pending.current = null;
    if (micNow && line.current && !line.current.agent) stopLine();
    const ln = pending.current && (!micNow || pending.current.ln.agent) ? pending.current.ln : null;
    if (ln) {
      pending.current = null;
      if (sh && Number(ln.a) === Number(sh.a) && (ln.mode || 'perform') === modeNow) {
        stopLine();
        const text = cleanText(ln.text);
        const name = ln.host?.name || sh.name;
        const nl = { id: ln.id, a: ln.a, mode: ln.mode || 'perform', name, text, el: null, agent: !!ln.agent, tried: performance.now(), until: performance.now() + speechMs(text) + (ln.audioUrl || ln.agent ? 4000 : TEXT_HOLD_MS) };
        if (ln.audioUrl) {
          const el = takeAudio(ln.audioUrl);
          nl.el = el;
          // Its real length, once known: the caption and the duck last exactly as long.
          onAudio(el, 'loadedmetadata', () => { if (Number.isFinite(el.duration) && el.duration > 0) nl.until = performance.now() + (el.duration - (el.currentTime || 0)) * 1000 + 500; }, { once: true });
          el.play().catch(() => { /* tried again below */ });
        }
        line.current = nl;
        // Something to say to you: they come over to say it (a visit, never a follow),
        // unless they are due at the mic, when they say it from the booth.
        if (nl.mode === 'mingle' && !visit.current && !micSoon) visit.current = { until: 0 };
        // Into the chat above the player bar, where you answer them.
        if (text) useWorldStore.getState().pushChat({ id: `host:${ln.a}`, handle: name, text, ts: Date.now() });
      }
    }
    let lineSpeaking = false;
    const L = line.current;
    if (L) {
      // The room changed under it (somebody came in, the set ended): cut, never over the set.
      if (!sh || Number(sh.a) !== Number(L.a) || modeNow !== L.mode) stopLine();
      else if (performance.now() > L.until || L.el?.ended) stopLine();
      else {
        // The agent's voice: as long as the call is speaking, the line is on.
        if (L.agent && venueAgent.speaking) L.until = Math.max(L.until, performance.now() + 800);
        if (L.el) {
          if (L.el.paused && !L.el.ended && performance.now() - L.tried > 2000) {
            L.tried = performance.now();
            L.el.play().catch(() => {});
          }
          setLevel(L.el, LINE_LEVEL * heard);
          if (!L.el.paused) { lineSpeaking = true; cs.setSpeaking(L.a, t + 400); }
        }
        caption = { name: L.name, text: L.text, hint: L.mode === 'mingle' ? `💬 to talk to ${firstName(L.name)}` : null };
      }
    }
    // The agent speaking (a line or not yet its text): the set ducks, their face moves.
    if (ming && venueAgent.speaking && Number(venueAgent.a) === Number(sh.a)) {
      lineSpeaking = true;
      cs.setSpeaking(sh.a, t + 400);
      if (!micSoon) {
        if (!visit.current) visit.current = { until: 0 };
        visit.current.until = Math.max(visit.current.until, performance.now() + LINGER_MS);
      }
    }
    // A visit lasts while they are talking and LINGER_MS after; it ends with the mingle.
    const nowMs = performance.now();
    if (visit.current) {
      if (line.current) visit.current.until = Math.max(visit.current.until, nowMs + LINGER_MS);
      if (!ming || micSoon || nowMs > visit.current.until) visit.current = null;
    }
    const visiting = ming && !!visit.current;
    // At the booth they are performing: the set at full level. Standing with you, it plays under.
    const want = (visiting ? MINGLE_LEVEL : 1) * (lineSpeaking ? DUCK : 1);
    mix.current += (want - mix.current) * MIX_EASE;

    if (sh && t >= sh.t0 - 30000) {
      const edge = Math.max(0, Math.min(1, (sh.t1 - t) / EDGE_MS, (t - sh.t0) / EDGE_MS + 0.3));
      // ── The song on now (and the next one, fetched ahead) ────────────────
      let i = -1;
      for (let q = 0; q < (sh.tracks || []).length; q++) if (t >= sh.t0 + sh.tracks[q].at) i = q;
      for (const q of [i, i + 1]) {
        const tr = sh.tracks?.[q];
        if (!tr?.url) continue;
        const key = `${sh.a}:${sh.t0}:${q}`;
        keep.add(key);
        let e = els.current.get(key);
        if (!e) {
          const el = takeAudio(tr.url);
          el.muted = true;
          try { el.load(); } catch { /* loads on play */ }
          e = { el, started: false };
          els.current.set(key, e);
        }
        if (q !== i) continue;
        const off = t - (sh.t0 + tr.at);
        // Between songs: silenced, so a file longer than its listed length never plays its tail over the words.
        if (off >= tr.durMs) { if (e.started) setLevel(e.el, 0); continue; }
        // Singing along at the booth; not while standing with you.
        if (!visiting) cs.setSpeaking(sh.a, sh.t0 + tr.at + tr.durMs);
        if (!e.started) {
          e.started = true;
          e.tried = performance.now();
          try { e.el.currentTime = off / 1000; } catch { /* on metadata */ }
          onAudio(e.el, 'loadedmetadata', () => { try { e.el.currentTime = (serverNow() - (sh.t0 + tr.at)) / 1000; } catch { /* ignore */ } }, { once: true });
          e.el.play().catch((err) => { e.playErr = err?.name || String(err); });
        } else if (e.el.paused && performance.now() - (e.tried || 0) > 2000) {
          // ⚠ A REFUSED START IS TRIED AGAIN (CityStage, 2026-10-30): every 2 s, from where the set is now.
          e.tried = performance.now();
          if (e.el.readyState >= 1) { try { e.el.currentTime = off / 1000; } catch { /* ignore */ } }
          e.el.play().then(() => { e.playErr = null; }).catch((err) => { e.playErr = err?.name || String(err); });
        } else if (e.el.readyState >= 2 && Math.abs(e.el.currentTime - off / 1000) > 0.8) {
          try { e.el.currentTime = off / 1000; } catch { /* ignore */ }
        }
        setLevel(e.el, SONG_LEVEL * edge * heard * mix.current);
        playing = { title: tr.title, art: tr.art || null, words: false };
      }
      // ── Their own words: the intro before each song, an outro if they said one ──
      // In BOTH modes (see "THE SHOW RUNS AS WRITTEN" above): alone, the host is
      // back at the booth for them.
      for (const [which, w] of micWords(sh)) {
        if (!w?.audio) continue;
        const start = sh.t0 + (w.at || 0);
        const end = start + (Number(w.dur) || 3) * 1000;
        if (t < start - 8000 || t > end + 500) continue;   // fetched 8 s ahead: the pool lends few players
        const key = `${sh.a}:${sh.t0}:${which}`;
        keep.add(key);
        let e = els.current.get(key);
        if (!e) {
          const el = takeAudio(w.audio);
          el.muted = true;
          try { el.load(); } catch { /* loads on play */ }
          e = { el, started: false };
          els.current.set(key, e);
        }
        if (t < start || t >= end) continue;
        cs.setSpeaking(sh.a, end);
        if (!e.started) {
          e.started = true;
          e.tried = performance.now();
          try { e.el.currentTime = (t - start) / 1000; } catch { /* on metadata */ }
          e.el.play().catch((err) => { e.playErr = err?.name || String(err); });
        } else if (e.el.paused && !e.el.ended && performance.now() - (e.tried || 0) > 2000) {
          e.tried = performance.now();
          if (e.el.readyState >= 1) { try { e.el.currentTime = (t - start) / 1000; } catch { /* ignore */ } }
          e.el.play().then(() => { e.playErr = null; }).catch((err) => { e.playErr = err?.name || String(err); });
        }
        setLevel(e.el, WORDS_LEVEL * heard);
        if (!caption) caption = { name: sh.name, text: cleanText(w.text) };
        playing = { title: 'on the mic', art: playing?.art || null, words: true };
      }
    }

    // Players for anything over: back to the pool.
    for (const [key, e] of els.current) {
      if (keep.has(key)) continue;
      releaseAudio(e.el);
      els.current.delete(key);
    }

    // ── The station steps aside the whole set long (gaps included) ─────────
    if (sh && !held.current) { held.current = true; audioEngine.roomQuiet(true, 900, HOLDER).catch?.(() => {}); }
    if (!sh && held.current) { held.current = false; audioEngine.roomQuiet(false, 900, HOLDER).catch?.(() => {}); }

    // ── Who stands at the mark: whoever the shared timeline puts there ─────
    let at = null;
    if (cs.live) {
      for (const r of cs.snap?.residents || []) {
        const st = stateAt(cs.byId?.[r.avatarId] || r, t, cs.convs);
        if (st?.inside === roomId && st.spot === 'booth') { at = { a: r.avatarId, name: r.name }; break; }
      }
    }
    if ((at?.a ?? null) !== (perf?.a ?? null)) setPerf(at);
    const tk = !!(at && (cs.speaking?.[at.a] || 0) > t);
    if (tk !== talking) setTalking(tk);

    // ── Mingling: a VISIT, not a follow (see LINGER_MS) ────────────────────
    // They come over ONCE, to between you and the booth, a conversation away,
    // and face you while they stay. Moving about does not move them; walking
    // away from them (LET_GO) ends the visit and they go back to the booth.
    if (visiting && mark && root.current && at && Number(at.a) === Number(sh.a)) {
      const pp = playerPos(state.camera);
      const P = root.current.worldToLocal(tmp.set(pp.x, pp.y, pp.z));
      const g = goal.current;
      if (!g) {
        const hx = mark.pos[0] * k - P.x; const hz = mark.pos[2] * k - P.z;
        const hd = Math.hypot(hx, hz);
        // Toward the booth from you (never past it); standing on the booth, in front of it.
        const ux = hd > 0.05 ? hx / hd : Math.sin(mark.rotY || 0);
        const uz = hd > 0.05 ? hz / hd : Math.cos(mark.rotY || 0);
        const near = Math.min(NEAR, hd > 0.05 ? hd : NEAR);
        goal.current = { x: P.x + ux * near, y: 0, z: P.z + uz * near, face: { x: P.x, z: P.z } };
      } else if (Math.hypot(P.x - g.x, P.z - g.z) > LET_GO) {
        visit.current = null;   // you walked off: they let you go
        goal.current = null;
      } else {
        g.face = { x: P.x, z: P.z };   // turn as you turn
      }
    } else if (goal.current) {
      goal.current = null;   // back to the booth
    }

    // ── What is on, for the screens, the player bar and the caption ────────
    const who = sh || next;
    const arts = who ? (who.tracks || []).map((x) => x.art).filter(Boolean) : [];
    const now = who ? {
      room: roomId, a: who.a, name: who.name, live: !!sh && t >= sh.t0,
      title: playing?.title || null, art: playing?.art || null, arts, words: !!playing?.words, kind: who.kind || null,
    } : null;
    const cur = cs.venueNow;
    if ((cur?.a ?? null) !== (now?.a ?? null) || (cur?.title ?? null) !== (now?.title ?? null) || (cur?.live ?? null) !== (now?.live ?? null) || (cur?.art ?? null) !== (now?.art ?? null)) {
      useCityStore.setState({ venueNow: now });
    }
    const where = visiting ? `${firstName(sh.name)} is hanging out with you · ${room?.name || 'the venue'}` : `Live · ${room?.name || 'the venue'}`;
    const bar = sh && playing ? { a: sh.a, name: sh.name, title: playing.title, art: playing.art, where } : null;
    if (bar || mine.current.stage) {
      const sn = cs.stageNow;
      if ((sn?.title ?? null) !== (bar?.title ?? null) || (sn?.a ?? null) !== (bar?.a ?? null) || (sn?.where ?? null) !== (bar?.where ?? null)) useCityStore.setState({ stageNow: bar });
      mine.current.stage = !!bar;
    }
    const cc = cs.venueCaption;
    if ((cc?.text ?? null) !== (caption?.text ?? null) || (cc?.hint ?? null) !== (caption?.hint ?? null)) useCityStore.setState({ venueCaption: caption });
  });

  // Mingling, a tap on them opens the chat in the player bar (PlayerBar listens).
  // Alone, they are approachable: a tap brings them over (unless they are due at the mic).
  const onTap = mingling && perf ? () => {
    if (!micSoonRef.current) visit.current = { until: performance.now() + LINGER_MS };
    window.dispatchEvent(new CustomEvent('ht:chat-open', { detail: { placeholder: `Say something to ${firstName(perf.name)}…` } }));
  } : null;

  const agent = <VenueAgent roomId={roomId} a={host?.a ?? null} name={host?.name || ''} mingling={!!host} />;
  if (!mark || !perf) return <group ref={root}>{agent}</group>;
  const light = (room?.light ?? 1) * (room?.model ? 0.45 : 1);
  return (
    <group ref={root}>
      {agent}
      <Performer key={perf.a} a={perf.a} name={perf.name} mark={mark} k={k} light={light} talking={talking} goal={goal} onTap={onTap} />
    </group>
  );
}

// THEIR WORDS, CAPTIONED (DOM, mounted in App beside RoomStoryAudio): what the
// performer is saying into the mic in the room you are in, at the bottom of the
// screen, like an inside story scene's lines — and, mingling, what they say to
// you, with how to answer.
const capWrap = {
  position: 'fixed', left: '50%', bottom: overKeys(), transition: 'bottom 0.35s ease',   // over the WASD keys when they're open (hudStack)
  transform: 'translateX(-50%)', zIndex: 35, maxWidth: 'min(560px, 88vw)', pointerEvents: 'none', textAlign: 'center',
  background: 'rgba(10,8,12,0.78)', border: '1px solid rgba(125,255,208,0.35)', borderRadius: 14, padding: '8px 16px 9px',
  color: '#eafff6', font: '15px system-ui, sans-serif', lineHeight: 1.4, backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
};

export function VenueStageCaption() {
  const cap = useCityStore((s) => s.venueCaption);
  const zoom = useWorldStore((s) => s.zoom);
  if (!cap?.text || zoom !== ZOOM.INTERIOR) return null;
  return (
    <div style={capWrap} role="status" aria-live="polite">
      {cap.name && <div style={{ fontSize: 10.5, letterSpacing: 1.2, textTransform: 'uppercase', opacity: 0.7, marginBottom: 2 }}>{cap.name}</div>}
      <div>{cap.text}</div>
      {cap.hint && <div style={{ fontSize: 11.5, opacity: 0.6, marginTop: 4 }}>{cap.hint}</div>}
    </div>
  );
}
